// https://www.material-react-table.com/docs/examples/infinite-scrolling

import { Stack, Typography } from '@mui/material';
import {
	QueryFunction,
	QueryKey,
	useInfiniteQuery,
} from '@tanstack/react-query';
import {
	EmptyTag,
	FeedbackTag,
	PointTag,
	ProductTag,
	RedeemTag,
	RewardTag,
} from 'components';
import { MaterialReactTable, MRT_ColumnDef } from 'material-react-table';
import { UIEvent, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { UnionInfiniteDataProps } from 'types';

interface APIResponse {
	data: {
		result: Array<UnionInfiniteDataProps>;
		totalCount: number;
	};
}

interface TagBoxContainerProps {
	queryKey: QueryKey;
	queryFn: QueryFunction<APIResponse>;
	TagComponent:
		| typeof ProductTag
		| typeof FeedbackTag
		| typeof RewardTag
		| typeof PointTag
		| typeof RedeemTag;
	boxHeight?: string;
}

const TagBoxContainer = ({
	queryKey,
	queryFn,
	TagComponent,
	boxHeight,
}: TagBoxContainerProps) => {
	const { t } = useTranslation();
	const tableContainerRef = useRef<HTMLDivElement>(null);

	const { data, fetchNextPage, isFetching } = useInfiniteQuery<APIResponse>({
		queryKey: queryKey,
		queryFn: queryFn,
		getNextPageParam: (_lastGroup, groups) => groups.length,
		keepPreviousData: true,
		refetchOnWindowFocus: false,
	});

	const flatData = useMemo(
		() => data?.pages.flatMap((page) => page.data.result) ?? [],
		[data]
	);

	const totalDBRowCount = data?.pages?.[0]?.data?.totalCount ?? 0;
	const totalFetched = flatData.length;

	const fetchMoreOnBottomReached = useCallback(
		(containerRefElement?: HTMLDivElement | null) => {
			if (containerRefElement) {
				const { scrollHeight, scrollTop, clientHeight } =
					containerRefElement;
				if (
					scrollHeight - scrollTop - clientHeight < 100 &&
					!isFetching &&
					totalFetched < totalDBRowCount
				) {
					fetchNextPage().then();
				}
			}
		},
		[fetchNextPage, isFetching, totalFetched, totalDBRowCount]
	);

	const columns = useMemo<MRT_ColumnDef<UnionInfiniteDataProps>[]>(
		() => [
			{
				accessorKey: 'id',
				header: '',
				Cell: ({ row }) => <TagComponent rowData={row.original} />,
			},
		],
		[]
	);

	if ((!data || totalDBRowCount === 0) && isFetching)
		return (
			<Stack alignItems="center" p={2} pt={1}>
				<EmptyTag />
			</Stack>
		);

	if (!data || totalDBRowCount === 0)
		return (
			<Stack alignItems="center" pt={10} pb={10}>
				<Typography>{t('history.no.record')}</Typography>
			</Stack>
		);

	return (
		<>
			<MaterialReactTable
				columns={columns}
				data={flatData}
				enablePagination={false}
				enableTopToolbar={false}
				enableBottomToolbar={false}
				enableRowSelection={false}
				muiTableHeadProps={{
					sx: { display: 'none' },
				}}
				muiTablePaperProps={{
					elevation: 0,
					sx: {
						borderRadius: '0',
						backgroundColor: 'rgb(0, 0, 0, 0.0)',
						// height: 'calc(100vh - 180px)',
						height: { boxHeight },
						overflow: 'scroll',
					},
					ref: tableContainerRef,
					onScroll: (event: UIEvent<HTMLDivElement>) =>
						fetchMoreOnBottomReached(
							event.target as HTMLDivElement
						),
				}}
				muiTableBodyRowProps={{
					hover: false,
					sx: {
						backgroundColor: 'rgb(0, 0, 0, 0.0)',
					},
				}}
				muiTableBodyCellProps={{
					sx: {
						border: 0,
						p: 1,
						pl: 2,
						pr: 2,
					},
				}}
			/>
		</>
	);
};

export default TagBoxContainer;
